import React, { useState, useRef, useCallback, useEffect } from 'react';
import { CircularLoader } from '@dhis2/ui';
import { DownloadIcon, XIcon } from 'lucide-react';
import {
  saveFilesToDataStore,
  loadFilesFromDataStore,
  getFileStorageKey,
  initializeFileStorage,
} from '../utils/fileStorage';
import { logPPNAction } from '../utils/ppnLogStorage';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = [
  'image/jpeg', 'image/png', 'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const FileAttachment = ({
  taskId,
  selectedWeek,
  objectiveId,
  kpiId,
  engine,
  onFileUploaded,
  onFileRemoved,
  isFormDisabled,
  showNotification,
  currentUser,
  isReadOnly = false,
  compact = false,
}) => {
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  const NAMESPACE = 'ppn-files';

  const getKey = useCallback(() => {
    const identifier = kpiId || taskId;
    if (!identifier || !objectiveId) return null;
    return getFileStorageKey(objectiveId, identifier, selectedWeek);
  }, [objectiveId, kpiId, taskId, selectedWeek]);

  const loadFiles = useCallback(async () => {
    const key = getKey();
    if (!key) {
      setFiles([]);
      return;
    }
    try {
      const loadedFiles = await loadFilesFromDataStore(engine, key, NAMESPACE);
      setFiles(loadedFiles || []);
    } catch (error) {
      setFiles([]);
    }
  }, [getKey, engine]);

  useEffect(() => {
    const init = async () => {
      await initializeFileStorage(engine);
      await loadFiles();
    };
    init();
  }, [loadFiles, engine]);

  const handleFileUpload = useCallback(
    async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > MAX_FILE_SIZE) {
        showNotification(`File too large. Max size is ${MAX_FILE_SIZE / 1024 / 1024}MB`, 'error');
        return;
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        showNotification('File type not supported.', 'error');
        return;
      }

      setIsLoading(true);
      setUploadProgress({ [file.name]: 0 });

      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const fileData = {
            id: `${file.name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: file.name,
            type: file.type,
            size: file.size,
            data: e.target.result,
            uploadedAt: new Date().toISOString(),
            uploadedBy: {
              id: currentUser?.id || 'unknown',
              name: currentUser?.displayName || currentUser?.username || 'Unknown',
            },
          };

          const key = getKey();
          if (!key) {
            showNotification('Missing objective or KPI/task ID', 'error');
            return;
          }
          const updatedFiles = [...files, fileData];

          await saveFilesToDataStore(engine, key, updatedFiles, NAMESPACE);
          setFiles(updatedFiles);

          await logPPNAction(
            engine,
            objectiveId,
            selectedWeek,
            'file_uploaded',
            {
              fileName: file.name,
              fileSize: file.size,
              taskId: taskId,
              fileId: fileData.id,
            },
            currentUser,
          );

          if (onFileUploaded) onFileUploaded(fileData);
          showNotification(`File "${file.name}" uploaded successfully`, 'success');
          setIsLoading(false);
          setUploadProgress({});
        };

        reader.onprogress = (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            setUploadProgress({ [file.name]: progress });
          }
        };

        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Upload error:', error);
        showNotification('Failed to upload file', 'error');
        setIsLoading(false);
        setUploadProgress({});
      }

      event.target.value = null;
    },
    [files, getKey, engine, objectiveId, selectedWeek, taskId, onFileUploaded, showNotification, currentUser],
  );

  const handleRemoveFile = useCallback(
    async (fileId) => {
      const fileToRemove = files.find((f) => f.id === fileId);
      if (!fileToRemove) return;

      const isOwner = fileToRemove.uploadedBy?.id === currentUser?.id;
      if (!isOwner) {
        showNotification('You are not allowed to delete this file.', 'error');
        return;
      }

      const updatedFiles = files.filter((f) => f.id !== fileId);
      try {
        const key = getKey();
        if (!key) {
          showNotification('Missing storage key', 'error');
          return;
        }
        await saveFilesToDataStore(engine, key, updatedFiles, NAMESPACE);
        setFiles(updatedFiles);

        await logPPNAction(
          engine,
          objectiveId,
          selectedWeek,
          'file_removed',
          {
            fileName: fileToRemove.name,
            fileId: fileId,
            taskId: taskId,
          },
          currentUser,
        );

        if (onFileRemoved) onFileRemoved(fileId);
        showNotification(`File "${fileToRemove.name}" removed`, 'success');
      } catch (error) {
        console.error('Removal error:', error);
        showNotification('Failed to remove file', 'error');
      }
    },
    [files, getKey, engine, objectiveId, selectedWeek, taskId, onFileRemoved, showNotification, currentUser],
  );

  const handleDownload = useCallback(
    (file) => {
      try {
        const link = document.createElement('a');
        link.href = file.data;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        showNotification('Failed to download file', 'error');
      }
    },
    [showNotification],
  );

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) return '🖼️';
    if (fileType === 'application/pdf') return '📄';
    if (fileType?.includes('word')) return '📝';
    if (fileType?.includes('excel')) return '📊';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ─── READ‑ONLY VIEW ──────────────────────────────────────
  if (isReadOnly) {
    return (
      <div style={{ marginTop: '4px' }}>
        {files.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', fontSize: '0.75rem' }}>
            {files.map((file) => (
              <div
                key={file.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '2px 8px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                }}
              >
                <span>{getFileIcon(file.type)}</span>
                <a
                  href={file.data}
                  download={file.name}
                  style={{ color: '#1976d2', textDecoration: 'none', cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownload(file);
                  }}
                >
                  {file.name}
                </a>
                <span style={{ fontSize: '0.6rem', color: '#999' }}>({formatFileSize(file.size)})</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '0.75rem', color: '#999', fontStyle: 'italic', padding: '4px 0' }}>
            No files attached
          </div>
        )}
      </div>
    );
  }

  // ─── FORM DISABLED ──────────────────────────────────────
  if (isFormDisabled) {
    return null;
  }

  // ─── EDITABLE VIEW ──────────────────────────────────────
  return (
    <div
      style={{
        marginBottom: compact ? '8px' : '20px',
        border: '1px solid #e0e0e0',
        borderRadius: compact ? '6px' : '10px',
        overflow: 'hidden',
        backgroundColor: '#fafafa',
      }}
    >
      {!compact && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#f5f5f5',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1.2rem' }}>📎</span>
            <span style={{ fontWeight: 600, color: '#333' }}>Attachments</span>
            <span
              style={{
                fontSize: '0.75rem',
                color: '#666',
                backgroundColor: '#e0e0e0',
                padding: '2px 8px',
                borderRadius: '12px',
              }}
            >
              {files.length}
            </span>
          </div>

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: '#4caf50',
              color: 'white',
              borderRadius: '12px',
              border: 'none',
              padding: '4px 12px',
              fontSize: '0.85rem',
              cursor: isLoading ? 'default' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isLoading ? 'Uploading...' : '+ Add File'}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            accept={ALLOWED_TYPES.join(',')}
            style={{ display: 'none' }}
          />
        </div>
      )}

      <div style={{ padding: compact ? '8px 12px' : '12px 16px' }}>
        {isLoading && Object.keys(uploadProgress).length > 0 && (
          <div
            style={{
              padding: '8px',
              backgroundColor: '#e3f2fd',
              borderRadius: '6px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <CircularLoader small />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.85rem' }}>Uploading: {Object.keys(uploadProgress)[0]}</div>
              <div
                style={{
                  width: '100%',
                  height: '4px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '2px',
                  marginTop: '4px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Object.values(uploadProgress)[0]}%`,
                    height: '100%',
                    backgroundColor: '#1976d2',
                    transition: 'width 0.3s',
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {compact && (
          <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              style={{
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                padding: '4px 14px',
                fontSize: '0.78rem',
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                cursor: isLoading ? 'default' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {isLoading ? 'Uploading...' : '+ Add File'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              accept={ALLOWED_TYPES.join(',')}
              style={{ display: 'none' }}
            />
          </div>
        )}

        {files.length === 0 && !isLoading && !compact && (
          <div
            style={{
              textAlign: 'center',
              padding: '24px 16px',
              color: '#999',
              fontSize: '0.9rem',
            }}
          >
            No files attached yet. Click "Add File" to upload evidence.
            <div style={{ fontSize: '0.75rem', color: '#bbb', marginTop: '4px' }}>
              Supports images, PDFs, Word, and Excel files (max 10MB)
            </div>
          </div>
        )}

        {files.length === 0 && !isLoading && compact && (
          <div style={{ textAlign: 'center', padding: '8px', color: '#999', fontSize: '0.8rem' }}>
            No files attached
          </div>
        )}

        {files.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {files.map((file) => {
              const isOwner = file.uploadedBy?.id === currentUser?.id;
              return (
                <div
                  key={file.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap', // allows wrapping on small screens
                    gap: '8px',
                    padding: compact ? '4px 8px' : '8px 12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e8e8e8',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#1976d2';
                    e.currentTarget.style.backgroundColor = '#f5f9ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e8e8e8';
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: '150px' }}>
                    <span style={{ fontSize: compact ? '1rem' : '1.5rem' }}>{getFileIcon(file.type)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 500,
                          color: '#333',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: compact ? '0.75rem' : '0.85rem',
                        }}
                      >
                        {file.name}
                      </div>
                      <div
                        style={{
                          fontSize: compact ? '0.6rem' : '0.7rem',
                          color: '#888',
                          marginTop: '2px',
                        }}
                      >
                        {formatFileSize(file.size)} • {new Date(file.uploadedAt).toLocaleDateString()}
                        {file.uploadedBy?.name && ` • by ${file.uploadedBy.name}`}
                      </div>
                    </div>
                  </div>

                  {/* Button container – always visible */}
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    <button
                      onClick={() => handleDownload(file)}
                      style={{
                        backgroundColor: '#22a211',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: compact ? '4px 8px' : '6px 12px',
                        color: '#fff',
                        fontSize: compact ? '0.75rem' : '0.8rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        minHeight: '32px',
                        minWidth: '32px',
                      }}
                    >
                      <DownloadIcon size={compact ? 16 : 18} />
                    </button>

                    {isOwner && (
                      <button
                        onClick={() => handleRemoveFile(file.id)}
                        style={{
                          backgroundColor: '#c90e20',
                          border: '1px solid #f5c6cb',
                          borderRadius: '4px',
                          padding: compact ? '4px 8px' : '6px 12px',
                          color: '#fff',
                          fontSize: compact ? '0.75rem' : '0.8rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          minHeight: '32px',
                          minWidth: '32px',
                        }}
                      >
                        <XIcon size={compact ? 16 : 18} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileAttachment;